import { useState } from "react";
import { Link } from "react-router-dom";
import { SERVICES, BUSINESS, WHY_US } from "../../mocks/businessData";
import TrustBar from "../home/components/TrustBar";
import AIAnswerBlock from "../home/components/AIAnswerBlock";
import ProcessSection from "../home/components/ProcessSection";
import WhyChooseUs from "../home/components/WhyChooseUs";
import ServiceAreasSection from "../home/components/ServiceAreasSection";
import TestimonialsSection from "../home/components/TestimonialsSection";
import FAQSection from "../home/components/FAQSection";
import QuoteFormSection from "../home/components/QuoteFormSection";
import { useScrollReveal } from "../../hooks/useScrollReveal";

const HERO_BG =
  "https://public.readdy.ai/ai/img_res/9a4eab66181bbe2cdb21792961d6d627.jpg";

export default function ServicesPage() {
  const [active, setActive] = useState(0);
  const ref = useScrollReveal();

  return (
    <main>
      {/* 1 — Hero */}
      <section
        className="relative pt-44 pb-32 overflow-hidden"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/90 via-stone-950/75 to-stone-950/92" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/50 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 text-left">
          <div className="hero-badge flex items-center gap-3 mb-8">
            <span className="inline-block w-10 h-px bg-amber-500" />
            <span className="text-amber-400 text-xs tracking-[0.40em] uppercase font-medium">
              Expert Window Treatment Restoration · NJ, NY, PA &amp; CT
            </span>
          </div>
          <h1 className="hero-h1 font-serif font-light text-5xl md:text-7xl lg:text-[5.5rem] text-white mb-6 leading-[1.03] max-w-4xl">
            Every Treatment,
            <span className="block italic text-amber-300 mt-1">Restored to Perfection.</span>
          </h1>
          <div className="hero-sub w-20 h-px bg-amber-500 mb-7" />
          <p className="hero-sub text-stone-300 text-base md:text-xl max-w-lg leading-relaxed font-light mb-12">
            Six expert cleaning services — all picked up from your home, restored at our facility,
            and rehung with precision and care.
          </p>
          <div className="hero-ctas flex flex-col sm:flex-row items-start gap-4">
            <a
              href="tel:19734891019"
              className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.25em] uppercase font-semibold px-10 py-4 rounded-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2" />
              {BUSINESS.phone}
            </a>
            <a
              href="#quote-form"
              onClick={(e) => { e.preventDefault(); document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" }); }}
              className="btn-luxury border border-white/30 hover:border-amber-400/70 bg-white/5 hover:bg-white/10 text-white hover:text-amber-300 text-xs tracking-[0.25em] uppercase font-semibold px-10 py-4 rounded-sm whitespace-nowrap cursor-pointer transition-colors duration-300"
            >
              Schedule Complimentary Pickup
            </a>
          </div>
        </div>
      </section>

      {/* 2 — Trust Bar */}
      <TrustBar />

      {/* 3 — Services Detail */}
      <section className="py-24 bg-white" ref={ref}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal text-center mb-14">
            <p className="text-amber-700 text-xs tracking-[0.40em] uppercase font-semibold mb-4">All 6 Services</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-stone-900 mb-5 leading-tight">
              What We Clean &amp;{" "}
              <span className="italic text-amber-700">Restore</span>
            </h2>
            <div className="gold-rule mx-auto mb-5" />
            <p className="text-stone-500 text-base max-w-xl mx-auto">
              Every service includes white-glove pickup, expert cleaning at our professional facility,
              and careful reinstallation at your convenience.
            </p>
          </div>

          {/* Tab selector */}
          <div className="reveal flex flex-wrap justify-center gap-2 mb-12">
            {SERVICES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActive(i)}
                className={`px-5 py-2.5 text-xs tracking-widest uppercase font-semibold rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap btn-luxury ${
                  active === i ? "bg-amber-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>

          {/* Active service panel */}
          <div className="reveal grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20">
            <div className="relative overflow-hidden h-80 lg:h-[440px] w-full rounded-sm">
              <img
                src={SERVICES[active].image}
                alt={SERVICES[active].title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent" />
              <div className="absolute top-5 left-5">
                <span className="bg-amber-700 text-white text-xs tracking-[0.2em] uppercase font-semibold px-3 py-1.5">
                  {SERVICES[active].title}
                </span>
              </div>
            </div>
            <div>
              <p className="text-amber-700/70 text-xs tracking-[0.28em] uppercase font-semibold mb-3">
                Service {String(active + 1).padStart(2, "0")} of {SERVICES.length}
              </p>
              <h3 className="font-serif font-light text-4xl md:text-5xl text-stone-900 mb-4 leading-tight">
                {SERVICES[active].title} Cleaning
              </h3>
              <div className="gold-rule mb-6" />
              <p className="text-stone-600 text-base leading-relaxed mb-4">{SERVICES[active].description}</p>
              <p className="text-stone-400 text-sm leading-relaxed mb-8">{SERVICES[active].fullDescription}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="tel:19734891019" className="btn-luxury bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-3.5 rounded-sm whitespace-nowrap cursor-pointer text-center">
                  <i className="ri-phone-fill mr-1.5" /> Call for Pricing
                </a>
                <a
                  href="#quote-form"
                  onClick={(e) => { e.preventDefault(); document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="btn-luxury border-2 border-stone-200 hover:border-amber-700 text-stone-700 hover:text-amber-700 text-xs tracking-[0.22em] uppercase font-semibold px-8 py-3.5 rounded-sm whitespace-nowrap cursor-pointer text-center transition-all duration-300"
                >
                  Free Estimate
                </a>
              </div>
            </div>
          </div>

          {/* All services mini grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SERVICES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActive(i)}
                className={`text-left border p-5 transition-all duration-300 cursor-pointer rounded-sm group depth-card service-card-hover ${
                  active === i ? "border-amber-300 bg-amber-50/50" : "border-stone-100 hover:border-amber-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-sm">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-stone-900 mb-1 group-hover:text-amber-800 transition-colors">{s.title}</h4>
                    <p className="text-stone-500 text-xs leading-relaxed line-clamp-2">{s.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — AI Answer Block */}
      <AIAnswerBlock />
      {/* 5 — Process */}
      <ProcessSection />
      {/* 6 — Why Choose Us */}
      <WhyChooseUs />
      {/* 7 — Service Areas */}
      <ServiceAreasSection />
      {/* 8 — Testimonials */}
      <TestimonialsSection />
      {/* 9 — FAQ */}
      <FAQSection />
      {/* 10 — Final CTA + Form */}
      <QuoteFormSection />
    </main>
  );
}
